
import Tabs from "@/components/Tabs/Tabs.vue";
import { EMPLOYEE_ROLE, EMPLOYER_ROLE } from "@/constants/roles";
import {
  EMPLOYEE_TABS,
  EMPLOYEE_TASKS_TABS,
  EMPLOYEES_TASKS_TAB,
  EMPLOYER_TABS,
  EMPLOYER_TASKS_TABS,
  MY_TASKS_TAB,
} from "@/constants/tasks";
import { TNewTask_Table, TTask_Table } from "@/models/tasks";
import { RCandidateServices } from "@/modules/Services/RServices";
import { SMTasks, SMUser } from "@/store";
import {
  computed,
  defineComponent,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue-demi";

const CASE_PRODUCT_TYPE = "case";
const VALUE_NAME = "name";
const VALUE_STATUS = "status";
const VALUE_SECTION = "complianceSection";
const VALUE_ACTION_BY = "actionBy";
const VALUE_DUE_DATE = "dueDate";

export default defineComponent({
  name: "VTasksList",

  components: { Tabs },

  beforeRouteEnter(to: any, _from: any, next: any) {
    next(async (vm: any) => {
      const companyId = Number(vm.$route?.params?.company_id ?? 0);
      if (companyId) SMTasks.setCompanyId(companyId);

      vm.loading = true;
      await vm.fetchTasks(true);
      vm.loading = false;

      const tabValues = vm.tabValues ?? [];
      const tabFromQuery = to.query?.tab;
      const validTab =
        tabFromQuery && tabValues.includes(tabFromQuery) ? tabFromQuery : tabValues[0] ?? "";
      vm.activeTab = validTab;
      if (tabFromQuery !== validTab && validTab) {
        vm.$router?.replace({ query: { ...vm.$route?.query, tab: validTab } });
      }
    });
  },

  setup() {
    const instance = getCurrentInstance()?.proxy as any;
    const route = computed(() => instance?.$route);
    const router = instance?.$router;

    const loading = ref(false);
    const activeTab = ref("");

    const userIsEmployer = computed(() => (SMUser as any).user?.role === EMPLOYER_ROLE);
    const companyId = computed(() => Number(route.value?.params?.company_id ?? 0));
    const tabValues = computed(() => (userIsEmployer.value ? EMPLOYER_TABS : EMPLOYEE_TABS));
    const tabNames = computed(() =>
      userIsEmployer.value ? EMPLOYER_TASKS_TABS : EMPLOYEE_TASKS_TABS
    );

    const mainTasks = computed(() => (SMTasks as any).myTasks ?? []);
    const sideTasks = computed(() => (SMTasks as any).sideTasks ?? []);
    const isComplianceBlocked = computed(
      () => (SMUser as any).user?.is_compliance_blocked ?? false
    );

    const complianceBlockedDescription = computed(() =>
      userIsEmployer.value
        ? "The Compliance subscription has expired. To continue staying compliant please contact the support team <a href='mailto:support@nationbetter.uk'>support@nationbetter.uk</a>"
        : "The company's Compliance subscription has expired"
    );

    function getTabCount(tabValue: string): number {
      if (tabValue === MY_TASKS_TAB) return mainTasks.value.length;
      if (tabValue === EMPLOYEES_TASKS_TAB) return sideTasks.value.length;
      if (tabValue === "employers_tasks") return sideTasks.value.length;
      return 0;
    }

    const tabItems = computed(() =>
      tabValues.value.map((value: string) => ({
        name: value,
        label: tabNames.value[value] || value,
        digit: getTabCount(value),
        isActive: activeTab.value === value,
      }))
    );

    const currentTabData = computed(() => {
      if (isComplianceBlocked.value) return [];
      if (activeTab.value === MY_TASKS_TAB) return mainTasks.value;
      return sideTasks.value;
    });

    const showTable = computed(() => {
      if (userIsEmployer.value && !mainTasks.value.length && !sideTasks.value.length) return false;
      return true;
    });

    function navigateTask(item: TNewTask_Table | TTask_Table) {
      const id = item?.id;
      if (id == null) return;
      let path = `${(SMUser as any).basePath}/tasks/${id}`;
      if ((item as TTask_Table)?.productType === CASE_PRODUCT_TYPE) {
        path = `/${RCandidateServices.path}/tasks/${id}`;
      }
      if (companyId.value) {
        path = `/expert/companies/${companyId.value}/tasks/${id}`;
      }
      router?.push(path);
    }

    const tableHeadersBase = computed(() => [
      {
        text: "Task name",
        value: VALUE_NAME,
        span: 3,
        action: navigateTask,
      },
      {
        text: "Status",
        value: VALUE_STATUS,
        filter: true,
        span: 0.5,
      },
      {
        text: "Compliance section",
        value: VALUE_SECTION,
      },
      {
        text: "Due date",
        value: VALUE_DUE_DATE,
        info: "Complete the task by that date to be compliant",
        infoPosition: "left",
        span: 0.5,
      },
    ]);

    const tableHeadersWithActionBy = computed(() => [
      {
        text: "Task name",
        value: VALUE_NAME,
        span: 2,
        action: navigateTask,
      },
      {
        text: "Status",
        value: VALUE_STATUS,
        filter: true,
        span: 0.5,
      },
      {
        text: "Compliance section",
        value: VALUE_SECTION,
      },
      {
        text: "Action by",
        value: VALUE_ACTION_BY,
        filter: true,
        filterSearch: true,
        span: 0.5,
      },
      {
        text: "Due date",
        value: VALUE_DUE_DATE,
        info: "Complete the task by that date to be compliant",
        infoPosition: "left",
        span: 0.5,
      },
    ]);

    const headers = computed(() => {
      const useActionBy = userIsEmployer.value && activeTab.value === EMPLOYEES_TASKS_TAB;
      const raw = useActionBy ? tableHeadersWithActionBy.value : tableHeadersBase.value;
      return raw.map((h: any) => ({
        label: h.text,
        value: h.value,
        sort: h.sort ?? false,
        sortBy: h.sortBy,
        filter: h.filter ?? false,
        info: h.info,
        infoPosition: h.infoPosition ?? "right",
        filterSearch: h.filterSearch ?? false,
        span: h.span,
        action: h.action,
      }));
    });

    function onTabSelect(value: string) {
      if (!value || route.value?.query?.tab === value) return;
      activeTab.value = value;
      router?.replace({ query: { ...route.value?.query, tab: value } });
    }

    function onTabSelectByIndex(index: number) {
      const value = tabValues.value[index];
      if (value) onTabSelect(value);
    }

    async function fetchTasks(prioritiseSide = false) {
      const myRole = userIsEmployer.value ? EMPLOYER_ROLE : EMPLOYEE_ROLE;
      const sideRole = userIsEmployer.value ? EMPLOYEE_ROLE : EMPLOYER_ROLE;
      loading.value = true;
      try {
        if (prioritiseSide) {
          await (SMTasks as any).getSideTasks({ role: sideRole });
          (SMTasks as any).getMyTasks({ role: myRole });
        } else {
          await (SMTasks as any).getMyTasks({ role: myRole });
          await (SMTasks as any).getSideTasks({ role: sideRole });
        }
      } finally {
        loading.value = false;
      }
    }

    watch(
      () => route.value?.query?.tab,
      (tab) => {
        if (tab && tabValues.value.includes(tab) && tab !== activeTab.value) {
          activeTab.value = tab;
        }
      },
      { immediate: true }
    );

    onMounted(() => {
      if (!activeTab.value && tabValues.value.length) {
        const tabFromQuery = route.value?.query?.tab;
        activeTab.value =
          tabFromQuery && tabValues.value.includes(tabFromQuery)
            ? tabFromQuery
            : tabValues.value[0];
      }
      instance?.$root?.$on?.("update-company-level-data", fetchTasks);
    });

    onBeforeUnmount(() => {
      instance?.$root?.$off?.("update-company-level-data", fetchTasks);
    });

    return {
      loading,
      activeTab,
      tabValues,
      tabItems,
      headers,
      currentTabData,
      showTable,
      isComplianceBlocked,
      complianceBlockedDescription,
      navigateTask,
      onTabSelect,
      onTabSelectByIndex,
      fetchTasks,
    };
  },
});
